'use client';

import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { EventManager } from '@mui/x-internals/EventManager';
import { jsxs as _jsxs } from "react/jsx-runtime";
export function createModal(config) {
  const Context = /*#__PURE__*/React.createContext(undefined);
  if (process.env.NODE_ENV !== "production") Context.displayName = "Context";
  function useModalContext() {
    const context = React.useContext(Context);
    if (!context) {
      throw /* FIXME (minify-errors-in-prod): Unminified error message in production build! */new Error(`MUI: \`${config.contextName}\` is missing. Hook must be placed within its Provider.`);
    }
    return context;
  }
  function Provider(props) {
    const {
      children,
      render,
      onOpen: onOpenProp,
      onClose: onCloseProp
    } = props;
    const anchorRef = React.useRef(null);
    const eventManager = React.useRef(new EventManager());
    const [state, setState] = React.useState({
      isOpen: false,
      data: null
    });
    const onOpen = useStableCallback((forwardedAnchorRef, data) => {
      anchorRef.current = forwardedAnchorRef?.current ?? null;
      setState({
        isOpen: true,
        data
      });
      onOpenProp?.(data);
      eventManager.current.emit('open', {
        data,
        anchorRef: anchorRef.current
      });
    });
    const onClose = useStableCallback(() => {
      onCloseProp?.();
      setState({
        isOpen: false,
        data: null
      });
      eventManager.current.emit('close');
    });
    const subscribeCloseHandler = React.useCallback(handler => {
      eventManager.current.on('close', handler);
      return () => {
        eventManager.current.removeListener('close', handler);
      };
    }, []);
    const contextValue = React.useMemo(() => ({
      onOpen,
      onClose,
      isOpen: state.isOpen || false,
      data: state.data,
      subscribeCloseHandler
    }), [onOpen, onClose, state.isOpen, state.data, subscribeCloseHandler]);
    return /*#__PURE__*/_jsxs(Context.Provider, {
      value: contextValue,
      children: [children, state.data && anchorRef.current && render({
        isOpen: Boolean(state.isOpen),
        anchorRef,
        data: state.data,
        onClose
      })]
    });
  }
  const Trigger = /*#__PURE__*/React.forwardRef(function Trigger(props, ref) {
    const {
      data,
      onClick,
      children
    } = props;
    const {
      onOpen
    } = useModalContext();
    return /*#__PURE__*/React.cloneElement(children, {
      ref,
      onClick: event => {
        onClick?.(event);
        onOpen(ref, data);
      }
    });
  });
  if (process.env.NODE_ENV !== "production") Trigger.displayName = "Trigger";
  return {
    Context,
    useContext: useModalContext,
    Provider,
    Trigger
  };
}